import { Modal } from 'antd';
import {
  useCreateTaskRecommendationMutation,
  useUpdateTaskRecommendationMutation,
} from 'app/api';
import { handleError } from 'app/containers/Global/slice/helpers';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { useTaskSlice } from 'app/containers/Task/slice';
import {
  selectTaskRecipe,
  selectUpdatedPlannedResourcesArr,
} from 'app/containers/Task/slice/selectors';
import {
  ICreateTaskPayload,
  ICreateTaskRecommendationBody,
  IEditTaskRecommendationBody,
  ITask,
  ITaskPlannedResource,
  RouteData,
} from 'app/containers/Task/slice/types';
import { RouteConstants } from 'app/routes';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, useHistory } from 'react-router-dom';
import { showCreateTaskSuccessNotification } from 'utils/notifications';
import { useCreateTask } from './useCreateTask';

export const useSubmitCreateOrUpdateTask = ({
  openResourcesTab,
  isEditPage,
  initialTask,
  isConsultant,
  taskRoutes,
}: {
  openResourcesTab: () => void;
  isEditPage: boolean;
  initialTask?: ITask;
  isConsultant?: boolean;
  taskRoutes: RouteData[];
}) => {
  const history = useHistory();
  const { t } = useTranslation();
  const { actions: taskActions } = useTaskSlice();
  const dispatch = useDispatch();

  //   State selectors
  const envId = useSelector(selectSelectedEnvId) as number;
  const taskRecipe = useSelector(selectTaskRecipe);
  const updatedPlannedResourcesArr: ITaskPlannedResource[] = useSelector(
    selectUpdatedPlannedResourcesArr,
  );

  // Mutations
  const [postTaskRecommendation] = useCreateTaskRecommendationMutation();
  const [updateTaskRecommendation] = useUpdateTaskRecommendationMutation();

  const { onTaskCreate } = useCreateTask();

  const createOrEditTaskRecommendation = async ({
    taskId,
    onSuccessRedirectTo,
    task: taskPayload,
  }: ICreateTaskPayload) => {
    try {
      const recipe = {
        id: taskRecipe?.id,
        resources: updatedPlannedResourcesArr.map(res => ({
          id: res.resourceId,
          norm: res.norm,
          price: res.price,
        })),
      };

      let response: ITask | null = null;

      if (taskId) {
        const { fieldSeasons, ...taskData } = taskPayload;
        const data: IEditTaskRecommendationBody = {
          ...taskData,
          recipe,
          taskFieldAddRemoveView: {
            fieldSeasonIds: fieldSeasons,
            removeFieldSeasonIds: (initialTask?.fieldTasks || [])
              .map(fieldTask => fieldTask.fieldSeason.id)
              .filter(
                id => !fieldSeasons.some(fieldSeason => fieldSeason === id),
              ),
          },
        };

        response = await updateTaskRecommendation({
          envId,
          data,
          taskId,
        }).unwrap();
      } else {
        const data: ICreateTaskRecommendationBody = {
          ...taskPayload,
          recipe,
        };

        response = await postTaskRecommendation({
          envId,
          data,
        }).unwrap();
      }

      showCreateTaskSuccessNotification();
      history.push(
        onSuccessRedirectTo ||
          generatePath(RouteConstants.task.details, {
            envId,
            taskId: response?.id || taskId,
          }),
      );
    } catch (error) {
      handleError(error);
    }
  };

  const createTask = (payload: ICreateTaskPayload) => {
    if (isConsultant) {
      // create recommendation
      createOrEditTaskRecommendation(payload);
    } else {
      // create task with status todo
      onTaskCreate({ payload, routeIds: taskRoutes.map(route => route.id) });
    }
  };

  const openPlannedResourcesReminderModal = (payload: ICreateTaskPayload) => {
    Modal.confirm({
      style: { width: 425 },
      title: t(
        'Field.CreateTask.ResourceReminder.Title',
        'Do you want add planned resources?',
      ),

      okText: t('Field.CreateTask.ResourceReminder.Yes', 'Yes'),
      cancelText: t('Field.CreateTask.ResourceReminder.No', 'No'),
      onOk() {
        openResourcesTab();
      },
      onCancel() {
        createTask(payload);
      },
    });
  };

  const onSubmit = ({ payload }: { payload: ICreateTaskPayload }) => {
    console.log(payload);

    if (isEditPage && initialTask) {
      if (isConsultant) {
        createOrEditTaskRecommendation({ taskId: initialTask.id, ...payload });
      } else {
        dispatch(
          taskActions.editTask({
            taskId: initialTask.id,
            ...payload,
          }),
        );
      }
    } else {
      if (!updatedPlannedResourcesArr.length) {
        openPlannedResourcesReminderModal(payload);
        return;
      }

      createTask(payload);
    }
  };

  return { onSubmit };
};
